
import React, { useState, useEffect } from 'react';
import { fetchProfile, deleteNews, createNews, updateNews } from '../utils/api';
import { formatDateTime } from '../utils/date';
import { ADMIN_ROLE, ADMISSION_STAFF, ACADEMIC_COORDINATOR_STAFF } from '../utils/roles';
import NewsModal from './NewsModal';
import './News.css';

const News = ({ news, onNewsDeleted, onNewsCreated }) => {
    const [profile, setProfile] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingNews, setEditingNews] = useState(null);

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const profileData = await fetchProfile();
                setProfile(profileData.data);
            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        loadProfile();
    }, []);

    const getPriorityClassName = (priority) => {
        switch (priority) {
            case '1st':
                return 'priority-1st';
            case '2nd':
                return 'priority-2nd';
            case '3rd':
                return 'priority-3rd';
            default:
                return '';
        }
    };

    const canManageNews = (newsItem) => {
        if (!profile) return false;
        const isAdmin = profile.roles.some(role => role.id === ADMIN_ROLE);
        const isAdmissionStaff = profile.roles.some(role => role.id === ADMISSION_STAFF);
        const isAcademicCoordinator = profile.roles.some(role => role.id === ACADEMIC_COORDINATOR_STAFF);
        const canCreate = isAdmin || isAdmissionStaff || isAcademicCoordinator;

        if (newsItem) {
            const isAuthor = newsItem.created_by.id === profile.id;
            return isAdmin || isAuthor;
        }
        return canCreate;
    };

    const handleDelete = async (newsId) => {
        if (window.confirm('Are you sure you want to delete this news item?')) {
            try {
                await deleteNews(newsId);
                onNewsDeleted();
            } catch (error) {
                console.error('Error deleting news:', error);
            }
        }
    };

    const handleCreateNews = () => {
        setEditingNews(null);
        setIsModalOpen(true);
    };

    const handleEditNews = (newsItem) => {
        setEditingNews(newsItem);
        setIsModalOpen(true);
    };

    const handleSaveNews = async (newsData) => {
        try {
            if (editingNews) {
                await updateNews(editingNews.id, newsData);
            } else {
                await createNews(newsData);
            }
            onNewsCreated();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error saving news:', error);
        }
    };

    return (
        <div className="news-container">
            <div className="notes-header">
                <h3>News</h3>
                {canManageNews(null) && (
                    <button onClick={handleCreateNews} className="submit-button text-button">Create News</button>
                )}
            </div>
            <div className="news-list">
                {news.length > 0 ? (
                    news.map(item => (
                        <div key={item.id} className={`news-card ${getPriorityClassName(item.priority.name)}`}>
                            <div className="news-header">
                                <h4>{item.title}</h4>
                                {canManageNews(item) && (
                                    <div className="news-actions">
                                        <button onClick={() => handleEditNews(item)} className="icon-button">📝</button>
                                        <button onClick={() => handleDelete(item.id)} className="icon-button">🗑️</button>
                                    </div>
                                )}
                            </div>
                            <p className="news-text">{item.text}</p>
                            <div className="news-meta">
                                <span>{item.created_by.first_name} {item.created_by.last_name}</span>
                                <span>{formatDateTime(item.publish_at || item.created_at)}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No news available.</p>
                )}
            </div>
            <NewsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveNews}
                newsItem={editingNews}
            />
        </div>
    );
};

export default News;
